import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { productsData } from "@/mocks/productsData";
import { fetchProductDetail, type ProductDetail as ApiProduct } from "@/api";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ApiProduct | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchProductDetail(id)
      .then((p) => setProduct(p))
      .catch((err) => {
        console.warn("产品详情加载失败，尝试本地数据:", err);
        const local = productsData.items.find((p) => p.id === id);
        if (local) setProduct(local as unknown as ApiProduct);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-32 px-4 md:px-8 lg:px-12 text-center">
          <p className="text-gray-500">Loading product...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-32 px-4 md:px-8 lg:px-12 text-center">
          <h1 className="text-2xl font-bold text-dark mb-4">Product Not Found</h1>
          <p className="text-gray-500 mb-6">
            The product you are looking for does not exist.
          </p>
          <button
            onClick={() => navigate("/products")}
            className="px-6 py-2.5 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors"
          >
            Back to Products
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <section className="pt-28 pb-6 px-4 md:px-8 lg:px-12 bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto flex items-center gap-2 text-sm text-gray-400">
            <button
              onClick={() => navigate("/")}
              className="hover:text-primary transition-colors"
            >
              Home
            </button>
            <i className="ri-arrow-right-s-line text-gray-300"></i>
            <button
              onClick={() => navigate("/products")}
              className="hover:text-primary transition-colors"
            >
              Products
            </button>
            <i className="ri-arrow-right-s-line text-gray-300"></i>
            <span className="text-dark font-medium">{product.name}</span>
          </div>
        </section>

        {/* Product Hero - Title + Image + Buttons */}
        <section className="py-10 md:py-14 px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            {/* Title above image */}
            <h1 className="text-3xl md:text-4xl font-bold text-dark mb-2 text-center">
              {product.name}
            </h1>
            <p className="text-primary text-sm font-medium text-center mb-8">
              {product.spec}
            </p>

            {/* Product Image */}
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-light-gray border border-gray-100 mb-8">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Buttons below image */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/contact"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/contact");
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary text-white font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                Request a Quote
              </a>
              <a
                href="/contact"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/contact");
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-full hover:border-primary hover:text-primary transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                Contact Sales Team
              </a>
            </div>
          </div>
        </section>

        {/* Product Details Poster */}
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-light-gray">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-dark mb-3">
                Product Details
              </h2>
              <p className="text-gray-500 text-sm max-w-lg mx-auto">
                Detailed specifications and highlights for the {product.name}
              </p>
            </div>

            {product.gallery && product.gallery.length > 0 ? (
              <div className="flex flex-col gap-0">
                {product.gallery.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`${product.name} Detail ${index + 1}`}
                    className="w-full h-auto"
                  />
                ))}
              </div>
            ) : (
              <img
                src={product.image}
                alt={`${product.name} Product Details`}
                className="w-full h-auto"
              />
            )}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-dark mb-4">
              Need More Information?
            </h2>
            <p className="text-gray-500 mb-8 max-w-2xl mx-auto">
              Our product specialists are ready to help you find the perfect
              solution for your communication needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/contact"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/contact");
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary text-white font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                Contact Sales Team
              </a>
              <a
                href="/products"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/products");
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-full hover:border-primary hover:text-primary transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Products
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}